import { closeMiniApp } from '@telegram-apps/sdk-react';

import type { SessionBlocker } from '@/api/rest';
import { isTelegramConfigured } from '@/telegram';
import { MainAction } from '@/ui/MainAction';

interface Copy {
  title: string;
  lead: string;
  body: string;
  /** null — действия нет: изнутри приложения этот тупик не разбирается. */
  action: string | null;
}

const COPY: Record<SessionBlocker, Copy> = {
  session: {
    title: 'ОТКРОЙТЕ ЗАНОВО',
    lead: 'ВХОД БОЛЬШЕ НЕ ПОДТВЕРЖДАЕТСЯ',
    // Формулировка без слова «сессия»: человеку за столом нужно действие, а не
    // диагноз. Telegram не обновляет данные входа, пока приложение открыто, —
    // поэтому единственное лекарство закрыть и открыть снова.
    body: 'Закройте приложение и откройте снова — Telegram выдаст свежие данные, и партия продолжится с того же места.',
    action: 'ЗАКРЫТЬ ПРИЛОЖЕНИЕ',
  },
  version: {
    title: 'ОБНОВИТЕ ПРИЛОЖЕНИЕ',
    lead: 'ВЕРСИЯ УСТАРЕЛА',
    body: 'Эта версия игры больше не понимает сервер. Обновите Telegram и откройте игру заново.',
    action: null,
  },
};

/**
 * Тупик: состояние, из которого приложение не выходит само (`auth-flow.md`
 * требует явный экран, а не белый). Перекрывает всё, включая возрастной гейт:
 * дальше по приложению всё равно ничего не работает, и предлагать человеку
 * подтверждать возраст перед неминуемым отказом незачем.
 *
 * Экрана нет в макете — обоих случаев не было в раскадровке; набор взят
 * гейтовый (`design-spec.md`, 01), потому что задача та же: одна мысль во весь
 * экран и одно действие внизу.
 */
export function BlockedScreen({ reason }: { reason: SessionBlocker }) {
  const copy = COPY[reason];
  // Внутри Telegram кнопка закрывает приложение; в обычном браузере её просто
  // нет, и экран остаётся читаемым текстом без мёртвого действия.
  const canClose = copy.action !== null && isTelegramConfigured() && closeMiniApp.isAvailable();

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)' }}
    >
      <div className="flex flex-1 flex-col justify-center gap-6 py-6">
        <p className="font-display text-meta uppercase text-acc">{copy.lead}</p>
        <h1 className="font-display text-turn">{copy.title}</h1>
        <p className="border-2 border-line bg-panel p-4 text-base text-dim">{copy.body}</p>
      </div>

      {canClose && copy.action && (
        <div className="-mx-5" style={{ paddingBottom: 'var(--clown-safe-bottom)' }}>
          <MainAction text={copy.action} onPress={() => closeMiniApp()} />
        </div>
      )}
    </div>
  );
}
